/**
 * Utility functions for handling review periods based on cadence
 */

export type ReviewCadence = "monthly" | "quarterly" | "biannual" | "annual";

/**
 * Generate review period string based on cadence and date
 * @param date - The review date
 * @param cadence - Review frequency: monthly, quarterly, biannual, annual
 * @returns Formatted period string appropriate for the cadence
 */
export function generateReviewPeriodString(date: Date, cadence: ReviewCadence): string {
  const month = date.getMonth();
  const year = date.getFullYear();

  switch (cadence) {
    case "monthly":
      // Format: "January 2024", "February 2024", etc.
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      return `${monthNames[month]} ${year}`;

    case "quarterly":
      // Format: "Q1 2024", "Q2 2024", etc.
      const quarter = Math.floor(month / 3) + 1;
      return `Q${quarter} ${year}`;

    case "biannual":
      // Format: "H1 2024" (first half), "H2 2024" (second half)
      const half = month < 6 ? 1 : 2;
      return `H${half} ${year}`;

    case "annual":
      // Format: "2024"
      return `${year}`;

    default:
      // Default to quarterly format
      const defaultQuarter = Math.floor(month / 3) + 1;
      return `Q${defaultQuarter} ${year}`;
  }
}

/**
 * Get current review period based on cadence
 * @param cadence - Review frequency
 * @returns Current period string
 */
export function getCurrentReviewPeriod(cadence: ReviewCadence = "quarterly"): string {
  return generateReviewPeriodString(new Date(), cadence);
}

/**
 * Generate review period options for a given cadence
 * @param cadence - Review frequency
 * @param yearsAhead - Number of years to generate options for (default: 4)
 * @returns Array of period strings
 */
export function getReviewPeriodOptions(cadence: ReviewCadence = "quarterly", yearsAhead: number = 4): string[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const periods: string[] = [];

  switch (cadence) {
    case "monthly":
      // Generate monthly options starting from current month
      for (let yearOffset = 0; yearOffset <= yearsAhead; yearOffset++) {
        const year = currentYear + yearOffset;
        const startMonth = yearOffset === 0 ? currentMonth : 0;
        const endMonth = yearOffset === yearsAhead ? 11 : 11;

        for (let month = startMonth; month <= endMonth; month++) {
          const date = new Date(year, month, 1);
          periods.push(generateReviewPeriodString(date, "monthly"));
        }
      }
      break;

    case "quarterly":
      // Generate quarterly options starting from current quarter
      const currentQuarter = Math.floor(currentMonth / 3) + 1;
      for (let yearOffset = 0; yearOffset <= yearsAhead; yearOffset++) {
        const year = currentYear + yearOffset;
        const startQuarter = yearOffset === 0 ? currentQuarter : 1;

        for (let q = startQuarter; q <= 4; q++) {
          periods.push(`Q${q} ${year}`);
        }
      }
      break;

    case "biannual":
      // Generate biannual options starting from current half
      const currentHalf = currentMonth < 6 ? 1 : 2;
      for (let yearOffset = 0; yearOffset <= yearsAhead; yearOffset++) {
        const year = currentYear + yearOffset;
        const startHalf = yearOffset === 0 ? currentHalf : 1;

        for (let h = startHalf; h <= 2; h++) {
          periods.push(`H${h} ${year}`);
        }
      }
      break;

    case "annual":
      // Generate annual options starting from current year
      for (let yearOffset = 0; yearOffset <= yearsAhead; yearOffset++) {
        periods.push(`${currentYear + yearOffset}`);
      }
      break;
  }

  return periods;
}

/**
 * Get human-readable label for review cadence
 * @param cadence - Review frequency
 * @returns Display label
 */
export function getReviewCadenceLabel(cadence: ReviewCadence): string {
  const labels: Record<ReviewCadence, string> = {
    monthly: "Monthly Review",
    quarterly: "Quarterly Review",
    biannual: "Biannual Review",
    annual: "Annual Review",
  };
  return labels[cadence] || "Business Review";
}

/**
 * Get short label for review period field
 * @param cadence - Review frequency
 * @returns Short label (e.g., "Quarter", "Month", "Period")
 */
export function getReviewPeriodLabel(cadence: ReviewCadence): string {
  const labels: Record<ReviewCadence, string> = {
    monthly: "Month",
    quarterly: "Quarter",
    biannual: "Half",
    annual: "Year",
  };
  return labels[cadence] || "Period";
}

/**
 * Display review period with fallback to quarter for backward compatibility
 * @param qbr - QBR object with reviewPeriod and/or quarter fields
 * @returns Display string for the review period
 */
export function getDisplayReviewPeriod(qbr: { reviewPeriod?: string | null; quarter?: string | null }): string {
  return qbr.reviewPeriod || qbr.quarter || "N/A";
}
